import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Users, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/flagged/photo-1579750481098-8b3a62c9b85d?auto=format&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-blue-900/80 to-slate-900/90"></div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              {t.home.hero.title}
            </h1>
            <p className="text-2xl md:text-3xl text-blue-200 font-light mb-4">
              {t.home.hero.subtitle}
            </p>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              {t.home.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/services">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg group">
                  {t.home.hero.cta}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  {t.home.hero.ctaSecondary}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              {t.home.intro.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t.home.intro.description}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {Object.values(t.home.stats).map((stat, index) => (
              <div key={index} className="text-center space-y-2 p-6 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="text-4xl md:text-5xl font-bold text-blue-600">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.services.title}</h2>
            <p className="text-lg text-gray-600">{t.services.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.services.categories.slice(0, 3).map((service, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500 group">
                <div className="space-y-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <CheckCircle className="h-6 w-6 text-blue-600 group-hover:text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                  <ul className="space-y-2">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                {language === 'fr' ? 'Voir tous les services' : 'View all services'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Aircraft Teaser */}
      <section
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1569154941061-e231b4725ef1?auto=format&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold">{t.aircraft.title}</h2>
            <p className="text-xl text-gray-200">{t.aircraft.subtitle}</p>
            <p className="text-lg text-gray-300 leading-relaxed">{t.aircraft.intro}</p>
            <Link to="/aircraft">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 mt-6">
                {language === 'fr' ? 'Découvrir nos capacités' : 'Discover our capabilities'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Notre Expertise' : 'Our Expertise'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'fr' ? 'Des solutions adaptées à tous les secteurs' : 'Solutions adapted to all sectors'}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Card className="p-8 text-center bg-gradient-to-br from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Énergie' : 'Energy'}
              </h3>
              <p className="text-blue-100 text-sm">
                {language === 'fr' ? 'Solutions solaires' : 'Solar solutions'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Logistique' : 'Logistics'}
              </h3>
              <p className="text-green-100 text-sm">
                {language === 'fr' ? 'Transport & flotte' : 'Transport & fleet'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Équipements' : 'Equipment'}
              </h3>
              <p className="text-purple-100 text-sm">
                {language === 'fr' ? 'Professionnels' : 'Professional'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Informatique' : 'IT'}
              </h3>
              <p className="text-orange-100 text-sm">
                {language === 'fr' ? 'Solutions complètes' : 'Complete solutions'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Maintenance' : 'Maintenance'}
              </h3>
              <p className="text-red-100 text-sm">
                {language === 'fr' ? 'Support 24/7' : '24/7 support'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Électroménager' : 'Appliances'}
              </h3>
              <p className="text-teal-100 text-sm">
                {language === 'fr' ? 'Professionnel' : 'Professional'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Véhicules' : 'Vehicles'}
              </h3>
              <p className="text-indigo-100 text-sm">
                {language === 'fr' ? 'Flotte complète' : 'Full fleet'}
              </p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-pink-600 to-pink-500 hover:from-pink-700 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Aéronefs' : 'Aircraft'}
              </h3>
              <p className="text-pink-100 text-sm">
                {language === 'fr' ? 'Solutions aériennes' : 'Air solutions'}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.about.values.title}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.about.values.items.map((value, index) => {
              const icons = [Award, TrendingUp, CheckCircle, Users];
              const Icon = icons[index];
              return (
                <div key={index} className="text-center space-y-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <Icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{value.name}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Prêt à commencer ?' : 'Ready to get started?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez-nous dès aujourd\'hui pour discuter de vos besoins'
              : 'Contact us today to discuss your needs'}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              {t.nav.contact}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;